import React, { useState } from 'react';
import { X } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-hot-toast';

const FormBeneficiaire = ({ onClose, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    nom: '',
    prenom: '',
    adresse: {
      rue: '',
      ville: '',
      codePostal: ''
    },
    telephone: '',
    email: '',
    consignes: '',
    contactUrgence: {
      nom: '',
      telephone: '',
      relation: ''
    },
    niveauVigilance: 'vert'
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    if (name.startsWith('adresse.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        adresse: {
          ...prev.adresse,
          [field]: value
        }
      }));
    } else if (name.startsWith('contactUrgence.')) {
      const field = name.split('.')[1];
      setFormData(prev => ({
        ...prev,
        contactUrgence: {
          ...prev.contactUrgence,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.nom || !formData.prenom) {
      toast.error('Nom et prénom sont obligatoires');
      return;
    }

    if (!formData.adresse.rue || !formData.adresse.ville || !formData.adresse.codePostal) {
      toast.error('Adresse complète requise (rue, ville, code postal)');
      return;
    }

    setLoading(true);
    try {
      await api.post('/beneficiaires', formData);
      toast.success('Bénéficiaire créé avec succès');
      onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur création bénéficiaire:', error);
      toast.error(error.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b px-6 py-4 flex justify-between items-center">
          <h2 className="text-xl font-bold">Nouveau Bénéficiaire</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Informations personnelles */}
          <div>
            <h3 className="font-semibold mb-3">Informations personnelles</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Nom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="nom"
                  value={formData.nom}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Prénom <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="prenom"
                  value={formData.prenom}
                  onChange={handleChange}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Téléphone</label>
                <input
                  type="tel"
                  name="telephone"
                  value={formData.telephone}
                  onChange={handleChange}
                  placeholder="06 12 34 56 78"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Email</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="prenom.nom@email.fr"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Adresse */}
          <div>
            <h3 className="font-semibold mb-3">Adresse</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">
                  Rue <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="adresse.rue"
                  value={formData.adresse.rue}
                  onChange={handleChange}
                  placeholder="12 rue de la Paix"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Code postal <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="adresse.codePostal"
                    value={formData.adresse.codePostal}
                    onChange={handleChange}
                    placeholder="69000"
                    maxLength={5}
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">
                    Ville <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="adresse.ville"
                    value={formData.adresse.ville}
                    onChange={handleChange}
                    placeholder="Lyon"
                    className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Vigilance */}
          <div>
            <label className="block text-sm font-medium mb-1">Niveau de vigilance</label>
            <select
              name="niveauVigilance"
              value={formData.niveauVigilance}
              onChange={handleChange}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="vert">🟢 Normal (Vert)</option>
              <option value="orange">🟠 Attention (Orange)</option>
              <option value="rouge">🔴 Urgent (Rouge)</option>
            </select>
          </div>

          {/* Consignes */}
          <div>
            <label className="block text-sm font-medium mb-1">Consignes particulières</label>
            <textarea
              name="consignes"
              value={formData.consignes}
              onChange={handleChange}
              rows="3"
              placeholder="Ex: Allergies, régime alimentaire, précautions..."
              className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {/* Contact d'urgence */}
          <div>
            <h3 className="font-semibold mb-3">Contact d'urgence</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Nom</label>
                <input
                  type="text"
                  name="contactUrgence.nom"
                  value={formData.contactUrgence.nom}
                  onChange={handleChange}
                  placeholder="Jean Dupont"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Téléphone</label>
                <input
                  type="tel"
                  name="contactUrgence.telephone"
                  value={formData.contactUrgence.telephone}
                  onChange={handleChange}
                  placeholder="06 12 34 56 78"
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">Relation</label>
                <input
                  type="text"
                  name="contactUrgence.relation"
                  value={formData.contactUrgence.relation}
                  onChange={handleChange}
                  placeholder="Fils, Fille..."
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border rounded-lg hover:bg-gray-50"
              disabled={loading}
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400"
              disabled={loading}
            >
              {loading ? 'Création...' : 'Créer le bénéficiaire'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default FormBeneficiaire;
